import { SwitchAccount } from "@mui/icons-material"
import { Box, Button, CircularProgress, Paper, Select, FormControl, InputLabel, MenuItem } from "@mui/material"
import Stack from "@mui/material/Stack"
import { DataGrid, GridColDef, GridRowsProp } from "@mui/x-data-grid"
import { useMeasure } from "@uidotdev/usehooks"
import useModalTrigger from "magik-react-hooks/useModalTrigger"
import { Fragment, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import CustomToolbar from "../components/CustomToolbar"
import DialogForm from "../components/DialogForm"
import Layout from "../components/Layout"
import { PageTitle } from "../components/PageTitle"
import Paginator from "../components/Paginator"
import ProduttoreForm from "../components/forms/ProduttoreForm"
import { useQsFilters } from "../hooks/filters"
import {
  useDownloadProduttori,
  useProduttoreCreate,
  useProduttoriList,
  useProduttoreDelete,
} from "../hooks/produttori"
import filesaver from "file-saver"
import { Fondo, Produttore } from "../types"
import DialogConfirm from "../components/DialogConfirm"
import { useSnackbarMessage } from "../hooks/snackbar"
import { Vocabolario } from "../types"
import useUserCanWriteAndDelete from "../hooks/permissions"
import IconButtonDelete from "../components/IconButtonDelete"
import LangLink from "../components/LangLink"
import { useIsMobile } from "../hooks/mobile"
import { ACVocabolario } from "../components/forms/autocompletes"
import { useIsTablet } from "../hooks/tablet"
import { useAuth } from "../auth"
import { PAGE_SIZE_OPTIONS, usePageSize } from "../hooks/pageSize"

const initFilters = (params: URLSearchParams) => ({
  page: Number(params.get("page") ?? 1),
  search: params.get("search") ?? "",
  ordering: params.get("ordering") ?? "titolo",
  tipologia: params.get("tipologia") ?? "",
  funzioni: params.get("funzioni") ?? "",
})

export default function Produttori() {
  const pageSize = usePageSize()
  const { filters, setFilters, uiFilters, setFiltersDebounced } = useQsFilters(initFilters)
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const userCanWriteAndDelete = useUserCanWriteAndDelete()
  const { user } = useAuth()
  const isMobile = useIsMobile() || user?.display === "mobile"
  const isTablet = (useIsTablet() || user?.display === "tablet") && !isMobile

  const columns: GridColDef[] = useMemo(() => {
    let cols: GridColDef[] = []
    cols.push({
      field: "cognome",
      headerName: t("produttore.cognome"),
      flex: 1,
      renderCell: (params) => (
        <LangLink to={`/produttori/${params.row.id}`} style={{ color: "black" }}>
          {params.row.cognome}
        </LangLink>
      ),
    })
    if (isMobile) {
      return cols
    }
    if (isTablet) {
      cols.push({
        field: "nome",
        headerName: t("produttore.nome"),
        flex: 1,
      })
      if (userCanWriteAndDelete) {
        cols.push({
          field: "id",
          headerName: t("general.azioni"),
          flex: 1,
          display: "flex",
          align: "right",
          headerAlign: "right",
          hideSortIcons: true,
          sortable: false,
          renderCell: (params) => (
            <IconButtonDelete onClick={() => deleteModalActions.open(params.row)} disabled={!params.row.can_delete} />
          ),
        })
      }
      return cols
    }
    cols.push(
      { field: "nome", headerName: t("produttore.nome"), flex: 1 },
      { field: "tipologia", headerName: t("produttore.tipologia"), flex: 1 },
      {
        field: "funzioni_data",
        headerName: t("produttore.funzioni"),
        sortable: false,
        flex: 1,
        renderCell: (params) => params.value.map((x: Vocabolario) => x.termine).join(", "),
      },
      {
        field: "fondi_data",
        headerName: t("donatore.fondi"),
        sortable: false,
        flex: 1,
        renderCell: (params) => {
          const fondi = params.row.fondi_data || ([] as Fondo[])
          return (
            <Box>
              {fondi.map((fondo: Fondo) => (
                <Fragment key={fondo.id}>
                  <LangLink key={fondo.id} style={{ color: "black" }} to={`/fondi/${fondo.id}`}>
                    {fondo.segnatura}
                  </LangLink>
                  {fondo !== fondi[fondi.length - 1] && ", "}
                </Fragment>
              ))}
            </Box>
          )
        },
      },
    )
    if (userCanWriteAndDelete) {
      cols.push({
        field: "id",
        headerName: t("general.azioni"),
        flex: 1,
        display: "flex",
        align: "right",
        headerAlign: "right",
        hideSortIcons: true,
        sortable: false,
        renderCell: (params) => (
          <IconButtonDelete onClick={() => deleteModalActions.open(params.row)} disabled={!params.row.can_delete} />
        ),
      })
    }
    return cols
  }, [t, userCanWriteAndDelete, isMobile, isTablet])

  const { data, isLoading } = useProduttoriList({
    page: String(filters.page),
    page_size: String(pageSize),
    search: filters.search,
    ordering: filters.ordering,
    tipologia: filters.tipologia,
    funzioni: filters.funzioni,
  })

  const { mutateAsync: downloadProduttori, isPending: isPendingDownload } = useDownloadProduttori({
    search: filters.search,
    ordering: filters.ordering,
    tipologia: filters.tipologia,
    funzioni: filters.funzioni,
  })

  const { mutateAsync: create } = useProduttoreCreate()
  const { mutateAsync: remove } = useProduttoreDelete()

  const numPages = useMemo(() => {
    if (!data) return 1
    return Math.ceil(data.count / pageSize)
  }, [data, pageSize])

  const [ref, { height }] = useMeasure()
  const [toolbarRef, { height: toolbarHeight }] = useMeasure()
  const tableHeight = useMemo(() => {
    if (!height || !toolbarHeight) return
    return height - toolbarHeight
  }, [height, toolbarHeight])

  const [createModalState, createModalActions] = useModalTrigger()
  const [deleteModalState, deleteModalActions] = useModalTrigger()

  const showMessage = useSnackbarMessage()

  const filtersCount = useMemo(() => {
    let count = 0
    if (filters.tipologia) count++
    if (filters.funzioni) count++

    return count
  }, [filters.tipologia, filters.funzioni])

  return (
    <Layout padding>
      <Stack sx={{ height: "100%" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <PageTitle
            title={t("produttore.titolo_lista")}
            breadcrumbs={[
              {
                label: t("general.produttori"),
                iconType: SwitchAccount,
              },
            ]}
          />
          <Box>
            {userCanWriteAndDelete && !isMobile && (
              <Button
                sx={{
                  marginRight: 1,
                }}
                variant="contained"
                disabled={isPendingDownload}
                color="secondary"
                onClick={() => {
                  downloadProduttori().then((blob) => {
                    filesaver.saveAs(blob, `produttori_${new Date().toISOString().split("T")[0]}.xlsx`)
                  })
                }}
              >
                {isPendingDownload ? <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} /> : null}
                {t("produttore.download_excel")}
              </Button>
            )}
            {userCanWriteAndDelete && !isMobile && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  createModalActions.open()
                }}
              >
                {t("produttore.cta_aggiungi")}
              </Button>
            )}
          </Box>
        </Box>
        <Box flexGrow={1}>
          <Paper sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <CustomToolbar
              count={data?.count}
              search={uiFilters.search}
              setSearch={(search) => setFiltersDebounced({ ...filters, search, page: 1 })}
              clearFilters={() => setFilters({ search: "", page: 1, tipologia: "", funzioni: "" })}
              filtersCount={filtersCount}
              ref={toolbarRef}
            >
              <Box display={"flex"} flexDirection={"row"} alignItems={"center"}>
                <FormControl
                  size="small"
                  sx={{
                    width: "200px",
                  }}
                >
                  <InputLabel id="demo-simple-select-label">{t("produttore.tipologia")}</InputLabel>
                  <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    value={uiFilters.tipologia}
                    label="Tipologia"
                    onChange={(e) => setFilters({ ...filters, tipologia: e.target.value, page: 1 })}
                  >
                    <MenuItem value={""}>{t("produttore.enum_tipologia.tutti")}</MenuItem>
                    <MenuItem value={"persona fisica"}>{t("produttore.enum_tipologia.persona_fisica")}</MenuItem>
                    <MenuItem value={"famiglia"}>{t("produttore.enum_tipologia.famiglia")}</MenuItem>
                    <MenuItem value={"ente"}>{t("produttore.enum_tipologia.ente")}</MenuItem>
                  </Select>
                </FormControl>
                <Box sx={{ ml: 2, width: "200px" }}>
                  <ACVocabolario
                    label={t("produttore.funzioni")}
                    vocabolario="funzioni"
                    margin="dense"
                    variant="outlined"
                    fullWidth
                    value={Number(filters.funzioni)}
                    onChange={(value) => {
                      if (!value) {
                        setFilters({ ...filters, funzioni: "", page: 1 })
                        return
                      }
                      setFilters({
                        ...filters,
                        funzioni: String(value),
                        page: 1,
                      })
                    }}
                    formComponentProps={{ vocabolario: "funzioni" }}
                  />
                </Box>
              </Box>
            </CustomToolbar>
            <div
              style={{
                flexGrow: 1,
                overflow: "scroll",
                height: tableHeight ?? undefined,
              }}
              ref={ref}
            >
              {height && (
                <DataGrid
                  disableColumnMenu
                  disableColumnResize
                  // density="compact"
                  pageSizeOptions={PAGE_SIZE_OPTIONS}
                  paginationModel={{ page: 0, pageSize }}
                  sx={{
                    padding: 2,
                    "&.MuiDataGrid-root": {
                      border: "none",
                    },
                  }}
                  localeText={{
                    noRowsLabel: t("general.nessun_dato_disponibile"),
                  }}
                  loading={isLoading}
                  slots={{
                    footer: () => (
                      <Paginator
                        numPages={numPages}
                        page={filters.page}
                        setPage={(page) =>
                          setFilters({
                            ...filters,
                            page,
                          })
                        }
                      />
                    ),
                    toolbar: null,
                  }}
                  rows={data?.results ?? ([] as GridRowsProp)}
                  columns={columns}
                  onSortModelChange={(model) => {
                    if (model.length > 0) {
                      const sortSign = model[0].sort === "asc" ? "" : "-"
                      const newOrdering = `${sortSign}${model[0].field}`
                      setFilters({ ordering: newOrdering, page: 1 })
                    }
                  }}
                />
              )}
            </div>
          </Paper>
        </Box>
      </Stack>

      <DialogForm<Produttore>
        open={createModalState.isOpen}
        handleClose={createModalActions.close}
        title={t("produttore.cta_aggiungi")}
        FormComponent={ProduttoreForm}
        hasDoubleLanguage
        save={(values: Produttore) => {
          return create(values).then((response) => {
            createModalActions.close()
            showMessage(t("produttore.esito_crea_ok"))
            navigate(`/${i18n.language}/produttori/${response.id}`)
          })
        }}
      />
      <DialogConfirm
        open={deleteModalState.isOpen}
        handleClose={deleteModalActions.close}
        title={t("produttore.cta_elimina")}
        subtitle={
          <span>
            {t("produttore.cta_subtitle_elimina")} <b>"{deleteModalState.value?.nome}"</b>&nbsp;?
          </span>
        }
        textConfirm={t("general.elimina")}
        onConfirm={() => {
          return remove(deleteModalState.value.id)
            .then(() => {
              deleteModalActions.close()
              showMessage(t("produttore.esito_elimina_ok"))
            })
            .catch(() => {
              deleteModalActions.close()
              showMessage(t("general.esito_elimina_errore"), "error")
            })
        }}
      />
    </Layout>
  )
}
